// Fil d'une conversation (charte D-038) : en-tête (type, objet rattaché, parties), bandeau du canal de
// crise et suivi de ses décisions consignées, participants, messages paginés regroupés par jour avec
// accusés de lecture, rédaction avec citation et pièces jointes, consignation d'une décision officielle
// (formulaire distinct, vérification puis confirmation explicite).

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Link, useSearchParams } from 'react-router';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TAILLE_MAX_DOCUMENT_OCTETS } from '@carburanttrack/shared';
import { useSession } from '../../app/session';
import { MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure } from '../../composants/format';
import { intervalleSecours, useEtatFlux } from '../notifications/tempsReel';
import {
  ajouterParticipant,
  cleJour,
  cles,
  codeFichier,
  collegues,
  consignerDecision,
  deposerPieceJointe,
  envoyerMessage,
  libelleJour,
  lireConversation,
  lireDecisions,
  lireMessages,
  marquerLue,
  retirerParticipant,
  taille,
  type DecisionVue,
  type DetailConversation,
  type MessageVue,
  type PieceJointeVue,
} from './api';
import { IconeConversation, IconeH2 } from './icones';
import { MessageItem, type Citation, type ContexteMessage } from './MessageItem';

const TYPES_ACCEPTES = '.pdf,.jpg,.jpeg,.png,.xlsx,.csv';
const PIECES_MAX = 10;

const LIBELLES_ROLES: Record<string, string> = {
  membre: 'Membre',
  animateur: 'Animateur',
  decideur: 'Décideur',
  emetteur: 'Émetteur',
  lecteur: 'Lecteur',
};

function nouvelIdentifiant(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // Repli sans crypto.randomUUID (contexte non sécurisé) : UUID v4 à partir de valeurs aléatoires.
  const octets = new Uint8Array(16);
  crypto.getRandomValues(octets);
  octets[6] = (octets[6]! & 0x0f) | 0x40;
  octets[8] = (octets[8]! & 0x3f) | 0x80;
  const h = [...octets].map((o) => o.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function Composeur({
  c,
  citation,
  annulerCitation,
  apresEnvoi,
}: {
  c: DetailConversation;
  citation: Citation | null;
  annulerCitation: () => void;
  apresEnvoi: () => void;
}) {
  const client = useQueryClient();
  const [texte, setTexte] = useState('');
  const [pieces, setPieces] = useState<PieceJointeVue[]>([]);
  const [depot, setDepot] = useState<{ enCours: string | null; erreur: unknown }>({
    enCours: null,
    erreur: null,
  });
  const fichier = useRef<HTMLInputElement>(null);
  const zone = useRef<HTMLTextAreaElement>(null);
  const identifiant = useRef(nouvelIdentifiant());
  const idAide = useId();
  const envoi = useMutation({
    mutationFn: () =>
      envoyerMessage(c.id, {
        contenu: texte,
        citeMessageId: citation?.id ?? null,
        piecesJointes: pieces.map((p) => p.id),
        identifiantClient: identifiant.current,
      }),
    onSuccess: async () => {
      setTexte('');
      setPieces([]);
      annulerCitation();
      // Un nouvel identifiant par message : un envoi répété après une coupure n'est enregistré qu'une fois.
      identifiant.current = nouvelIdentifiant();
      await Promise.all([
        client.invalidateQueries({ queryKey: cles.messages(c.id) }),
        client.invalidateQueries({ queryKey: ['messagerie', 'conversations'] }),
        client.invalidateQueries({ queryKey: cles.statut }),
      ]);
      apresEnvoi();
    },
  });

  useEffect(() => {
    if (citation) zone.current?.focus();
  }, [citation]);

  async function choisirFichiers(liste: FileList | null) {
    const fichiers = [...(liste ?? [])];
    if (fichier.current) fichier.current.value = '';
    for (const f of fichiers) {
      if (pieces.length >= PIECES_MAX) {
        setDepot({ enCours: null, erreur: new Error(`${PIECES_MAX} pièces jointes au plus par message.`) });
        return;
      }
      if (f.size > TAILLE_MAX_DOCUMENT_OCTETS) {
        setDepot({ enCours: null, erreur: new Error(`${f.name} dépasse 10 Mo.`) });
        return;
      }
      setDepot({ enCours: f.name, erreur: null });
      try {
        const pj = await deposerPieceJointe(c.id, f);
        setPieces((p) => [...p, pj]);
        setDepot({ enCours: null, erreur: null });
      } catch (e) {
        setDepot({ enCours: null, erreur: e });
        return;
      }
    }
  }

  const peutEnvoyer =
    (texte.trim().length > 0 || pieces.length > 0) && !envoi.isPending && depot.enCours === null;
  const soumettre = (e?: FormEvent) => {
    e?.preventDefault();
    if (peutEnvoyer) envoi.mutate();
  };
  const raccourci = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) soumettre();
  };
  const lignes = Math.min(6, Math.max(2, texte.split('\n').length));

  return (
    <form className="msg-composeur" onSubmit={soumettre} aria-label="Rédiger un message">
      {citation && (
        <div className="msg-citation-brouillon">
          <IconeH2 nom="citation" taille={16} />
          <span>
            <b>Réponse à {citation.auteur ?? 'la notification automatique'}</b> {citation.extrait}
          </span>
          <button type="button" className="bouton discret" onClick={annulerCitation}>
            Retirer la citation
          </button>
        </div>
      )}
      {(pieces.length > 0 || depot.enCours) && (
        <ul className="msg-pieces-brouillon" aria-label="Pièces jointes du message">
          {pieces.map((p) => (
            <li key={p.id}>
              <span className="msg-piece-code" aria-hidden="true">
                {codeFichier(p.typeFichier)}
              </span>
              <span>
                {p.nomFichier} <small className="attenue">{taille(p.taille)}</small>
              </span>
              <button
                type="button"
                className="bouton discret"
                aria-label={`Retirer ${p.nomFichier}`}
                onClick={() => setPieces((l) => l.filter((x) => x.id !== p.id))}
              >
                Retirer
              </button>
            </li>
          ))}
          {depot.enCours && (
            <li role="status">
              <span>Dépôt chiffré de {depot.enCours} en cours</span>
            </li>
          )}
        </ul>
      )}
      <div className="msg-saisie">
        <button
          type="button"
          className="bouton msg-joindre"
          aria-label="Joindre un fichier"
          disabled={depot.enCours !== null || pieces.length >= PIECES_MAX}
          onClick={() => fichier.current?.click()}
        >
          <IconeH2 nom="trombone" />
        </button>
        <input
          ref={fichier}
          type="file"
          multiple
          accept={TYPES_ACCEPTES}
          className="visuellement-cache"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => void choisirFichiers(e.target.files)}
        />
        <label htmlFor={`composeur-${c.id}`} className="visuellement-cache">
          Votre message
        </label>
        <textarea
          ref={zone}
          id={`composeur-${c.id}`}
          value={texte}
          rows={lignes}
          onChange={(e) => setTexte(e.target.value)}
          onKeyDown={raccourci}
          maxLength={10000}
          placeholder="Écrire un message"
          aria-describedby={idAide}
        />
        <button type="submit" className="bouton primaire msg-envoyer" disabled={!peutEnvoyer}>
          <IconeH2 nom="envoyer" />
          <span className="libelle-bouton">{envoi.isPending ? 'Envoi' : 'Envoyer'}</span>
        </button>
      </div>
      <p id={idAide} className="msg-aide-composeur">
        PDF, JPEG, PNG, XLSX ou CSV, 10 Mo au plus par fichier, type réel vérifié. Ctrl et Entrée pour
        envoyer.
      </p>
      {depot.erreur !== null && <MessageErreur erreur={depot.erreur} titre="Pièce jointe refusée" />}
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Envoi refusé" />}
    </form>
  );
}

function FormulaireDecision({
  c,
  decisions,
  fermer,
}: {
  c: DetailConversation;
  decisions: DecisionVue[];
  fermer: () => void;
}) {
  const client = useQueryClient();
  const [titre, setTitre] = useState('');
  const [contenu, setContenu] = useState('');
  const [responsable, setResponsable] = useState('');
  const [echeance, setEcheance] = useState('');
  const [corrigee, setCorrigee] = useState('');
  const [etape, setEtape] = useState<'saisie' | 'verification'>('saisie');
  const [confirme, setConfirme] = useState(false);
  const idListe = useId();
  const consignation = useMutation({
    mutationFn: () =>
      consignerDecision(c.id, {
        titre: titre.trim(),
        contenu: contenu.trim(),
        responsable: responsable.trim(),
        echeance: new Date(echeance).toISOString(),
        decisionCorrigeeId: corrigee || null,
      }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: cles.messages(c.id) }),
        client.invalidateQueries({ queryKey: cles.decisions(c.id) }),
      ]);
      fermer();
    },
  });
  const echeanceValide = echeance !== '' && !Number.isNaN(new Date(echeance).getTime());
  const complet =
    titre.trim().length >= 3 &&
    contenu.trim().length >= 3 &&
    responsable.trim().length >= 2 &&
    echeanceValide;
  const choisie = decisions.find((d) => d.id === corrigee);

  return (
    <section className="carte-bloc msg-formulaire-decision" aria-labelledby={`decision-${c.id}`}>
      <div className="carte-bloc-tete">
        <h2 id={`decision-${c.id}`}>Consigner une décision officielle</h2>
        <button type="button" className="bouton discret" onClick={fermer}>
          Fermer
        </button>
      </div>
      <div className="etapes" aria-label="Étapes de la consignation">
        <button
          type="button"
          aria-current={etape === 'saisie' ? 'step' : undefined}
          className={etape === 'verification' ? 'faite' : undefined}
          onClick={() => setEtape('saisie')}
        >
          <span className="numero">1</span>Saisie
        </button>
        <button
          type="button"
          aria-current={etape === 'verification' ? 'step' : undefined}
          disabled={!complet}
          onClick={() => complet && setEtape('verification')}
        >
          <span className="numero">2</span>Vérification et confirmation
        </button>
      </div>
      <div className="note ambre">
        <IconeH2 nom="cadenas" />
        <span>
          Une décision consignée est horodatée, diffusée aux membres de la cellule et ne peut plus être
          modifiée ni masquée. Une erreur se corrige par une nouvelle décision liée.
        </span>
      </div>
      {etape === 'saisie' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (complet) setEtape('verification');
          }}
        >
          <div className="champ">
            <label htmlFor="decision-titre">Intitulé</label>
            <input
              id="decision-titre"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              maxLength={200}
              required
              minLength={3}
            />
          </div>
          <div className="champ">
            <label htmlFor="decision-contenu">Contenu de la décision</label>
            <textarea
              id="decision-contenu"
              value={contenu}
              onChange={(e) => setContenu(e.target.value)}
              maxLength={10000}
              required
              minLength={3}
            />
          </div>
          <div className="formulaire-ligne">
            <div className="champ">
              <label htmlFor="decision-responsable">Responsable de l’exécution</label>
              <input
                id="decision-responsable"
                list={idListe}
                value={responsable}
                onChange={(e) => setResponsable(e.target.value)}
                maxLength={200}
                required
                minLength={2}
              />
              <datalist id={idListe}>
                {c.participants.map((p) => (
                  <option
                    key={p.utilisateurId}
                    value={p.fonction ? `${p.prenom} ${p.nom}, ${p.fonction}` : `${p.prenom} ${p.nom}`}
                  />
                ))}
              </datalist>
              <span className="aide">
                Une personne ou une fonction ; les membres de la cellule sont proposés.
              </span>
            </div>
            <div className="champ">
              <label htmlFor="decision-echeance">Échéance</label>
              <input
                id="decision-echeance"
                type="datetime-local"
                value={echeance}
                onChange={(e) => setEcheance(e.target.value)}
                required
              />
            </div>
          </div>
          {decisions.length > 0 && (
            <div className="champ">
              <label htmlFor="decision-corrigee">Corrige une décision antérieure</label>
              <select id="decision-corrigee" value={corrigee} onChange={(e) => setCorrigee(e.target.value)}>
                <option value="">Aucune, décision nouvelle</option>
                {decisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.titre} ({dateHeure(d.creeLe)})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="actions">
            <button type="submit" className="bouton primaire" disabled={!complet}>
              Vérifier avant consignation
            </button>
            <button type="button" className="bouton" onClick={fermer}>
              Annuler
            </button>
          </div>
        </form>
      ) : (
        <div>
          <dl className="paires">
            <div>
              <dt>Intitulé</dt>
              <dd>{titre}</dd>
            </div>
            <div>
              <dt>Responsable</dt>
              <dd>{responsable}</dd>
            </div>
            <div>
              <dt>Échéance</dt>
              <dd>{dateHeure(new Date(echeance).toISOString())}</dd>
            </div>
            {choisie && (
              <div>
                <dt>Corrige</dt>
                <dd>{choisie.titre}</dd>
              </div>
            )}
          </dl>
          <p className="msg-texte msg-apercu-decision">{contenu}</p>
          <label className="case msg-confirmation">
            <input type="checkbox" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} />
            <span>
              Je confirme la consignation de cette décision officielle.
              <small>Elle sera horodatée à son enregistrement et ne pourra plus être modifiée.</small>
            </span>
          </label>
          <div className="actions">
            <button
              type="button"
              className="bouton primaire"
              disabled={!confirme || consignation.isPending}
              onClick={() => consignation.mutate()}
            >
              <IconeH2 nom="cadenas" />
              Consigner définitivement
            </button>
            <button type="button" className="bouton" onClick={() => setEtape('saisie')}>
              Revenir à la saisie
            </button>
          </div>
          {consignation.error && <MessageErreur erreur={consignation.error} titre="Consignation refusée" />}
        </div>
      )}
    </section>
  );
}

function Participants({ c, fermer }: { c: DetailConversation; fermer: () => void }) {
  const client = useQueryClient();
  const [ajout, setAjout] = useState('');
  const membres = useQuery({
    queryKey: ['messagerie', 'collegues'],
    queryFn: collegues,
    enabled: c.peutGererParticipants && c.type === 'groupe',
  });
  const actualiser = () => client.invalidateQueries({ queryKey: cles.conversation(c.id) });
  const retrait = useMutation({
    mutationFn: (u: string) => retirerParticipant(c.id, u),
    onSuccess: actualiser,
  });
  const ajoutM = useMutation({
    mutationFn: (u: string) => ajouterParticipant(c.id, u, 'membre'),
    onSuccess: async () => {
      setAjout('');
      await actualiser();
    },
  });
  const presents = new Set(c.participants.map((p) => p.utilisateurId));
  const candidats = membres.data?.filter((m) => !presents.has(m.id)) ?? [];
  return (
    <section className="carte-bloc msg-panneau" aria-labelledby={`participants-${c.id}`}>
      <div className="carte-bloc-tete">
        <h2 id={`participants-${c.id}`}>
          {c.type === 'service' ? 'Personnes ayant ouvert le canal' : 'Participants'}
        </h2>
        <button type="button" className="bouton discret" onClick={fermer}>
          Fermer
        </button>
      </div>
      {c.type === 'service' && (
        <p className="contexte">
          Le canal est ouvert à toutes les personnes des organisations parties à l’objet, dans leur périmètre
          : {c.organisationsParties.join(', ')}.
        </p>
      )}
      {c.type === 'crise' && (
        <p className="contexte">
          Les membres de la cellule sont désignés à son activation ; seul l’animateur en retire.
        </p>
      )}
      {c.participants.length === 0 ? (
        <p className="attenue">Personne n’a encore ouvert ce canal.</p>
      ) : (
        <ul className="msg-participants">
          {c.participants.map((p) => (
            <li key={p.utilisateurId}>
              <span className="initiales" aria-hidden="true">
                {`${p.prenom.charAt(0)}${p.nom.charAt(0)}`.toUpperCase()}
              </span>
              <span className="msg-participant-texte">
                <b>
                  {p.prenom} {p.nom}
                </b>
                <small>
                  {p.fonction ?? 'Fonction non renseignée'}
                  {p.dernierLuLe ? `, lu le ${dateHeure(p.dernierLuLe)}` : ', aucune lecture'}
                </small>
              </span>
              <span className="etiquette">{LIBELLES_ROLES[p.role] ?? p.role}</span>
              {c.peutGererParticipants && p.role !== 'animateur' && (
                <button
                  type="button"
                  className="bouton discret"
                  disabled={retrait.isPending}
                  onClick={() => retrait.mutate(p.utilisateurId)}
                  aria-label={`Retirer ${p.prenom} ${p.nom}`}
                >
                  Retirer
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {c.peutGererParticipants && c.type === 'groupe' && membres.data && (
        <form
          className="msg-ajout-participant"
          onSubmit={(e) => {
            e.preventDefault();
            if (ajout) ajoutM.mutate(ajout);
          }}
        >
          <div className="champ">
            <label htmlFor={`ajout-${c.id}`}>Ajouter un membre de votre organisation</label>
            <select
              id={`ajout-${c.id}`}
              value={ajout}
              onChange={(e) => setAjout(e.target.value)}
              disabled={candidats.length === 0}
            >
              <option value="">
                {candidats.length === 0 ? 'Tous les membres sont déjà présents' : 'Choisir une personne'}
              </option>
              {candidats.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.prenom} {m.nom}
                  {m.fonction ? `, ${m.fonction}` : ''}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="bouton" disabled={!ajout || ajoutM.isPending}>
            <Icone nom="ajout" />
            Ajouter
          </button>
        </form>
      )}
      {(retrait.error || ajoutM.error) && (
        <MessageErreur erreur={retrait.error ?? ajoutM.error} titre="Modification refusée" />
      )}
    </section>
  );
}

function SuiviDecisions({
  decisions,
  surAllerA,
}: {
  decisions: DecisionVue[];
  surAllerA: (id: string) => void;
}) {
  // Replié par défaut : la plus récente décision reste lisible en une ligne au-dessus du fil.
  const [ouvert, setOuvert] = useState(false);
  const idListe = useId();
  if (decisions.length === 0) {
    return (
      <div className="msg-suivi-decisions msg-suivi-vide">
        <span className="sur-titre">
          <IconeH2 nom="cadenas" taille={15} />
          Aucune décision officielle consignée pour l’instant
        </span>
      </div>
    );
  }
  const recente = decisions[0]!;
  return (
    <section className="msg-suivi-decisions" aria-label="Décisions consignées">
      <button
        type="button"
        className="msg-suivi-tete"
        aria-expanded={ouvert}
        aria-controls={idListe}
        onClick={() => setOuvert((v) => !v)}
      >
        <span className="msg-suivi-resume">
          <span className="sur-titre">
            <IconeH2 nom="cadenas" taille={15} />
            Décisions consignées ({decisions.length})
          </span>
          {!ouvert && (
            <span className="msg-suivi-recente">
              Dernière : <b>{recente.titre}</b>, échéance {dateHeure(recente.echeance)}
            </span>
          )}
        </span>
        <span className="contexte">{ouvert ? 'Replier' : 'Tout afficher'}</span>
      </button>
      {ouvert && (
        <ol id={idListe} className="msg-suivi-liste">
          {decisions.map((d) => (
            <li key={d.id} className={d.corrigeePar.length > 0 ? 'corrigee' : undefined}>
              <button type="button" className="msg-suivi-carte" onClick={() => surAllerA(d.id)}>
                <b>{d.titre}</b>
                <small>
                  {d.responsable}, échéance {dateHeure(d.echeance)}
                </small>
                <small>Consignée le {dateHeure(d.creeLe)}</small>
                {d.corrigeePar.length > 0 && <span className="etiquette ambre">Corrigée</span>}
                {d.corrigeeId && <span className="etiquette">Correction</span>}
              </button>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** Conversation individuelle : le titre est le nom de l'autre personne ; sinon le titre enregistré. */
function titreAffiche(c: DetailConversation, moi: string | undefined): string {
  if (c.type !== 'individuelle') return c.titre;
  const autre = c.participants.find((p) => p.utilisateurId !== moi);
  return autre ? `${autre.prenom} ${autre.nom}` : c.titre;
}

function ligneContexte(c: DetailConversation): string {
  if (c.type === 'annonces') {
    if (c.monRole === 'emetteur')
      return `Émise par ${c.organisationEmettrice ?? 'votre organisation'} vers ${c.nombreOrganisationsParties ?? 0} organisations, en lecture pour les destinataires`;
    return `Annonce de ${c.organisationEmettrice ?? 'l’administration'}, en lecture seule`;
  }
  if (c.type === 'service' || c.type === 'crise') return `Parties : ${c.organisationsParties.join(', ')}`;
  if (c.type === 'individuelle') {
    const fonctions = c.participants.map((p) =>
      p.fonction ? `${p.prenom} ${p.nom}, ${p.fonction}` : `${p.prenom} ${p.nom}`,
    );
    return `Conversation individuelle : ${fonctions.join(' ; ')}`;
  }
  return `Groupe interne : ${c.participants.map((p) => `${p.prenom} ${p.nom}`).join(', ')}`;
}

/** Messages regroupés par jour, du plus ancien au plus récent. */
function parJour(messages: MessageVue[]): { cle: string; libelle: string; messages: MessageVue[] }[] {
  const groupes: { cle: string; libelle: string; messages: MessageVue[] }[] = [];
  for (const m of messages) {
    const cle = cleJour(m.creeLe);
    const dernier = groupes[groupes.length - 1];
    if (dernier && dernier.cle === cle) dernier.messages.push(m);
    else groupes.push({ cle, libelle: libelleJour(m.creeLe), messages: [m] });
  }
  return groupes;
}

export function FilConversation({ id }: { id: string }) {
  const { etat } = useSession();
  const moi = etat?.utilisateur?.id;
  const client = useQueryClient();
  const etatFlux = useEtatFlux();
  const [params, setParams] = useSearchParams();
  const messageVise = params.get('message');
  const [citation, setCitation] = useState<Citation | null>(null);
  const [panneau, setPanneau] = useState<'aucun' | 'participants' | 'decision'>('aucun');
  const [cible, setCible] = useState<string | null>(null);
  const [aTrouver, setATrouver] = useState<string | null>(null);
  const conteneur = useRef<HTMLDivElement>(null);
  // Fil complet : sur téléphone, la page défile jusqu'au bas du fil (composeur compris).
  const fin = useRef<HTMLDivElement>(null);
  const secours = intervalleSecours(etatFlux);

  const detail = useQuery({
    queryKey: cles.conversation(id),
    queryFn: () => lireConversation(id),
    refetchInterval: secours,
  });
  const fil = useInfiniteQuery({
    queryKey: cles.messages(id),
    queryFn: ({ pageParam }) => lireMessages(id, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (derniere) => (derniere.meta.curseurSuivant as string | null) ?? undefined,
    refetchInterval: secours,
  });
  const estCrise = detail.data?.type === 'crise';
  const suivi = useQuery({
    queryKey: cles.decisions(id),
    queryFn: () => lireDecisions(id),
    enabled: estCrise,
    refetchInterval: secours,
  });
  const messages = useMemo(
    () =>
      fil.data
        ? fil.data.pages
            .flatMap((p) => p.donnees)
            .slice()
            .reverse()
        : [],
    [fil.data],
  );
  const dernierId = messages.at(-1)?.id;
  const decisions = useMemo(() => suivi.data ?? [], [suivi.data]);

  const allerEnBas = useCallback(() => {
    const c = conteneur.current;
    if (c && c.scrollHeight > c.clientHeight + 1) c.scrollTop = c.scrollHeight;
    else fin.current?.scrollIntoView?.({ block: 'end' });
  }, []);

  const surAllerA = useCallback((messageId: string) => {
    setATrouver(messageId);
  }, []);

  // Message visé (citation, décision, résultat de recherche) : chargé page après page s'il est ancien.
  useEffect(() => {
    if (messageVise) setATrouver(messageVise);
  }, [messageVise]);
  useEffect(() => {
    if (!aTrouver || !fil.data) return;
    const element = document.getElementById(`message-${aTrouver}`);
    if (element) {
      element.scrollIntoView?.({ block: 'center' });
      setCible(aTrouver);
      setATrouver(null);
      if (messageVise) {
        const suite = new URLSearchParams(params);
        suite.delete('message');
        setParams(suite, { replace: true });
      }
      return;
    }
    if (fil.hasNextPage && !fil.isFetchingNextPage && fil.data.pages.length < 25) void fil.fetchNextPage();
    else if (!fil.hasNextPage) setATrouver(null);
  }, [aTrouver, fil, messageVise, params, setParams]);

  // Accusé de lecture : la conversation ouverte et visible est marquée lue à chaque nouveau message.
  useEffect(() => {
    if (!dernierId) return;
    const marquer = () => {
      if (document.visibilityState === 'hidden') return;
      marquerLue(id)
        .then(() =>
          Promise.all([
            client.invalidateQueries({ queryKey: ['messagerie', 'conversations'] }),
            client.invalidateQueries({ queryKey: cles.statut }),
            client.invalidateQueries({ queryKey: ['notifications'] }),
          ]),
        )
        .catch(() => undefined);
    };
    marquer();
    document.addEventListener('visibilitychange', marquer);
    return () => document.removeEventListener('visibilitychange', marquer);
  }, [id, dernierId, client]);

  // Nouveau message : le fil descend jusqu'au plus récent, sauf pendant la recherche d'un message visé.
  useEffect(() => {
    if (dernierId && !aTrouver && !messageVise) allerEnBas();
  }, [dernierId, aTrouver, messageVise, allerEnBas]);

  useEffect(() => {
    setCitation(null);
    setPanneau('aucun');
    setCible(null);
  }, [id]);

  if (detail.isLoading) return <Squelette lignes={6} libelle="Ouverture de la conversation" />;
  if (detail.error) {
    return (
      <div className="msg-fil">
        <Link to="/messages" className="bouton discret msg-retour">
          <Icone nom="retour" />
          Conversations
        </Link>
        <MessageErreur erreur={detail.error} titre="Conversation inaccessible" />
      </div>
    );
  }
  const c = detail.data!;
  const ctx: ContexteMessage = {
    conversationId: c.id,
    moi,
    surCiter: c.peutEcrire ? setCitation : null,
    surAllerA,
    decisions,
  };

  return (
    <div className={`msg-fil msg-type-${c.type}`} ref={fin}>
      <header className="msg-entete">
        <Link to="/messages" className="bouton discret msg-retour">
          <Icone nom="retour" />
          Conversations
        </Link>
        <span className="msg-entete-picto" aria-hidden="true">
          <IconeConversation type={c.type} objetType={c.objetType} />
        </span>
        <div className="msg-entete-texte">
          <span className="sur-titre">
            {c.libelleType}
            {c.libelleObjet ? `, ${c.libelleObjet}` : ''}
          </span>
          <h2>{titreAffiche(c, moi)}</h2>
          <p className="contexte">{ligneContexte(c)}</p>
        </div>
        <div className="actions msg-entete-actions">
          {c.peutConsignerDecision && (
            <button
              type="button"
              className="bouton primaire"
              aria-expanded={panneau === 'decision'}
              onClick={() => setPanneau((p) => (p === 'decision' ? 'aucun' : 'decision'))}
            >
              <IconeH2 nom="cadenas" />
              Consigner une décision
            </button>
          )}
          {(c.type !== 'annonces' || c.monRole === 'emetteur') && (
            <button
              type="button"
              className="bouton"
              aria-expanded={panneau === 'participants'}
              onClick={() => setPanneau((p) => (p === 'participants' ? 'aucun' : 'participants'))}
            >
              <IconeH2 nom="groupe" />
              <span>Participants ({c.participants.length})</span>
            </button>
          )}
        </div>
      </header>
      {/* Exceptions d'abord : les décisions en vigueur restent visibles au-dessus du fil. */}
      {c.type === 'crise' &&
        (suivi.error ? (
          <MessageErreur erreur={suivi.error} titre="Suivi des décisions indisponible" />
        ) : (
          suivi.data && <SuiviDecisions decisions={decisions} surAllerA={surAllerA} />
        ))}
      <div className="msg-defilement" ref={conteneur}>
        {c.type === 'crise' && (
          <div className="msg-bandeau-crise" role="note">
            <IconeH2 nom="crise" />
            <span>
              <b>Canal de crise.</b> Seul espace où des organisations concurrentes échangent ; les décisions
              officielles y sont consignées par les décideurs et ne sont plus modifiables.
            </span>
          </div>
        )}
        {c.statut === 'archive' && (
          <div className="note">
            <Icone nom="document" />
            <span>
              Conversation archivée le {dateHeure(c.archiveeLe)} après 12 mois sans activité. Un nouveau
              message la réactive.
            </span>
          </div>
        )}
        {panneau === 'participants' && <Participants c={c} fermer={() => setPanneau('aucun')} />}
        {panneau === 'decision' && (
          <FormulaireDecision c={c} decisions={decisions} fermer={() => setPanneau('aucun')} />
        )}
        <div className="msg-messages" aria-live="polite" aria-relevant="additions" aria-busy={fil.isFetching}>
          {fil.hasNextPage && (
            <div className="msg-precedents">
              <button
                type="button"
                className="bouton"
                disabled={fil.isFetchingNextPage}
                onClick={() => void fil.fetchNextPage()}
              >
                {fil.isFetchingNextPage ? 'Chargement' : 'Messages précédents'}
              </button>
            </div>
          )}
          {fil.isLoading && <Squelette lignes={4} libelle="Chargement des messages" />}
          {fil.error && <MessageErreur erreur={fil.error} titre="Messages indisponibles" />}
          {fil.data && messages.length === 0 && (
            <p className="attenue msg-vide">
              Aucun message pour l’instant. Le premier message ouvre l’échange.
            </p>
          )}
          <ol className="msg-liste-messages">
            {parJour(messages).map((g) => (
              <li key={g.cle} className="msg-groupe-jour">
                <h3 className="msg-jour">
                  <span>{g.libelle}</span>
                </h3>
                <ol className="msg-liste-jour">
                  {g.messages.map((m) => (
                    <MessageItem key={m.id} m={m} ctx={ctx} cible={m.id === cible} />
                  ))}
                </ol>
              </li>
            ))}
          </ol>
        </div>
      </div>
      {c.peutEcrire ? (
        <Composeur
          c={c}
          citation={citation}
          annulerCitation={() => setCitation(null)}
          apresEnvoi={allerEnBas}
        />
      ) : (
        <p className="msg-lecture-seule">
          <IconeH2 nom="cadenas" taille={16} />
          {c.motifLecture ? `Lecture seule : ${c.motifLecture}.` : 'Lecture seule.'}
        </p>
      )}
    </div>
  );
}
